import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, Wrench } from "lucide-react";

const DEMOS = [
    { label: "Customer", id: "9811111111", pw: "homiie123" },
    { label: "Professional", id: "9822222222", pw: "homiie123" },
];

export default function Login() {
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const { login, user, logout } = useAuth();
    const { t } = useLang();
    const nav = useNavigate();
    const loc = useLocation();

    const go = (u) => {
        const role = u.role;
        const from = loc.state?.from;
        if (role === "admin") nav("/admin");
        else if (role === "professional") nav("/pro");
        else if (u.guardian_of) nav("/care");
        else nav(from && !from.startsWith("/login") ? from : "/");
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await login(identifier.trim(), password);
            toast.success(`Welcome back, ${u.name}!`);
            go(u);
        } catch (err) {
            toast.error(apiError(err.response?.data?.detail) || "Login failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center bg-lightbg px-6 py-10">
            <div className="mb-8">
                <Logo size={46} />
                <h1 className="mt-6 font-heading text-3xl font-extrabold text-navy">{t("Welcome back")}</h1>
                <p className="mt-1 text-sm text-navy/55">{t("Log in to manage your home services.")}</p>
            </div>

            {user && (
                <div data-testid="login-already-signed-in" className="mb-5 rounded-2xl border border-teal/20 bg-white p-4 text-sm text-navy">
                    <p>You're signed in as <b>{user.name}</b>{user.phone ? ` (${user.phone})` : ""}.</p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                        <button type="button" data-testid="login-continue-btn" onClick={() => go(user)} className="rounded-full bg-teal py-2.5 text-xs font-bold text-white">Continue as {user.name?.split(" ")[0]}</button>
                        <button type="button" data-testid="login-switch-btn" onClick={async () => { await logout(); toast.success("Logged out. Log in with another account."); }} className="rounded-full border border-navy/15 py-2.5 text-xs font-bold">Log out & switch</button>
                    </div>
                </div>
            )}

            <form onSubmit={submit} className="space-y-4">
                <div>
                    <label className="mb-1.5 block text-sm font-semibold text-navy">{t("Phone number or email")}</label>
                    <input
                        data-testid="login-identifier-input"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        placeholder="98xxxxxxxx"
                        className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy outline-none transition-colors focus:border-orange"
                        required
                    />
                </div>
                <div>
                    <label className="mb-1.5 block text-sm font-semibold text-navy">{t("Password")}</label>
                    <input
                        data-testid="login-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-navy outline-none transition-colors focus:border-orange"
                        required
                    />
                    <Link to="/reset-password" data-testid="forgot-password-link" className="mt-1.5 block text-right text-xs font-bold text-orange">Forgot password?</Link>
                </div>
                <button
                    data-testid="login-submit-btn"
                    disabled={busy}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white shadow-lg shadow-orange/30 transition-transform active:scale-95 disabled:opacity-60"
                >
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t("LOG IN")}
                </button>
            </form>

            <p className="mt-5 text-center text-sm text-navy/60">
                {t("New to HOMIIE?")}{" "}
                <Link to="/register" data-testid="go-register-link" className="font-bold text-orange">{t("Create account")}</Link>
            </p>

            <Link to="/join-pro" data-testid="join-pro-link" className="mt-4 flex items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 transition-colors hover:border-orange">
                <div className="rounded-xl bg-orange/10 p-2.5"><Wrench size={20} className="text-orange" /></div>
                <div className="flex-1">
                    <h3 className="font-heading text-sm font-bold text-navy">{t("Become a HOMIIE Professional")}</h3>
                    <p className="text-xs text-navy/50">{t("Earn with verified, steady jobs near you")}</p>
                </div>
            </Link>

            <div className="mt-6 rounded-2xl border border-navy/10 bg-white p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-navy/50">{t("Demo logins — tap to fill")}</p>
                <div className="flex flex-wrap gap-2">
                    {DEMOS.map((d) => (
                        <button
                            key={d.label}
                            data-testid={`demo-${d.label.toLowerCase()}-btn`}
                            onClick={() => { setIdentifier(d.id); setPassword(d.pw); }}
                            className="rounded-full border border-navy/15 px-3 py-1.5 text-xs font-semibold text-navy transition-colors hover:border-orange hover:text-orange"
                        >
                            {d.label}
                        </button>
                    ))}
                </div>
            </div>
            <Link to="/" className="mt-6 text-center text-xs text-navy/40">← {t("Back to home")}</Link>
        </div>
    );
}
